// @ts-check
// Página de uma campanha de e-mail (/campaigns/email/:id; spec/email.md seção 19.7; contrato 09 de e-mail, seção 10):
// a vista sai da situação. Rascunho: o assistente (o rascunho da 1.13.0 é atualizado ao abrir, com o aviso). Agendada:
// resumo com "Desagendar e editar", "Enviar teste" e "Parar". Não enviada: o motivo, "Voltar para rascunho" e
// "Duplicar". Em preparação, envio, pausada, concluída ou cancelada: o relatório.
import { html } from 'htm/preact';
import { useEffect, useState } from 'preact/hooks';
import { Link, navigate } from '../../app/router.js';
import { ApiError } from '../../data/api-client.js';
import {
  backToDraftEmailCampaign, cancelEmailCampaign, duplicateEmailCampaign, getEmailCampaign, unscheduleEmailCampaign, upgradeEmailCampaign,
} from '../../data/email.js';
import { fieldsById, viewsState } from '../../data/metadata-store.js';
import { workspaceMembersState, loadWorkspaceMembers } from '../../data/workspace-members.js';
import { summarizeAudience } from '../../lib/audience-summary.js';
import { emailCampaignPageView } from '../../lib/email-campaigns.js';
import { formatFullName } from '../../lib/field-metadata.js';
import { formatDateTime } from '../../lib/format.js';
import { Button } from '../../ui/button.js';
import { EmptyState, Skeleton } from '../../ui/feedback.js';
import { Icon } from '../../ui/icon.js';
import { Modal } from '../../ui/modal.js';
import { showErrorSnackbar, showSuccessSnackbar } from '../../ui/snackbar.js';
import { PageCard, PageHeader } from '../page/page-card.js';
import { EmailCampaignReport } from './campaign-report.js';
import { EmailCampaignTestDialog } from './campaign-test-dialog.js';
import { EmailCampaignWizard } from './campaign-wizard.js';
import { EmailCampaignActionsMenu, EmailCampaignStatusBadge } from './campaigns-page.js';
import { emailSettingsState, loadEmailSettings } from '../email-design/email-branding.js';
import { hasTranslation, t } from '../../i18n/index.js';

/** @typedef {import('../../data/email.js').EmailCampaign} EmailCampaign */

const LIST_PATH = '/campaigns/email';

/** @param {string | null | undefined} workspaceMemberId */
function memberName(workspaceMemberId) {
  const member = (workspaceMembersState.value ?? []).find((item) => item.id === workspaceMemberId);
  return member ? formatFullName(member.name) || member.userEmail || '' : '';
}

/** Resumo da agendada. @param {{ campaign: EmailCampaign, onChanged: (campaign: EmailCampaign) => void }} props */
function ScheduledView({ campaign, onChanged }) {
  const [dialog, setDialog] = useState(/** @type {'TEST' | 'CANCEL' | null} */ (null));
  const [isBusy, setIsBusy] = useState(false);
  const audience = campaign.audienceDefinition
    ? summarizeAudience(campaign.audienceDefinition, { getField: (id) => fieldsById.value.get(id), views: viewsState.value })
    : [];
  /** @param {() => Promise<EmailCampaign>} run @param {string} message */
  const execute = async (run, message) => {
    setIsBusy(true);
    try {
      const updated = await run();
      showSuccessSnackbar(message);
      setDialog(null);
      onChanged(updated);
    } catch (error) {
      showErrorSnackbar(error);
    } finally {
      setIsBusy(false);
    }
  };
  return html`<div class="ec-scheduled" data-testid="email-campaign-scheduled">
    <div class="ec-notice" data-tone="blue" role="status">
      <${Icon} name="IconCalendarTime" size=${18} />
      <div>
        <strong>${t('emailCampaigns.scheduled.title', { date: campaign.scheduledAt ? formatDateTime(campaign.scheduledAt) : '' })}</strong>
        <p class="ec-muted">${t('emailCampaigns.scheduled.hint')}</p>
        ${memberName(campaign.sentByWorkspaceMemberId) ? html`<p class="ec-muted">${t('emailCampaigns.scheduled.by', { name: memberName(campaign.sentByWorkspaceMemberId) })}</p>` : null}
      </div>
    </div>
    <dl class="ec-summary-list">
      <dt>${t('emailCampaigns.steps.audience')}</dt><dd>${audience.join(' · ')}</dd>
      <dt>${t('emailCampaigns.sender.subject')}</dt><dd>${campaign.subject}</dd>
      ${campaign.preheader ? html`<dt>${t('emailCampaigns.sender.preheader')}</dt><dd>${campaign.preheader}</dd>` : null}
    </dl>
    <div class="ec-button-row">
      <${Button} title=${t('emailCampaigns.actions.unscheduleAndEdit')} icon="IconPencil" size="small" variant="secondary" isLoading=${isBusy}
        onClick=${() => execute(() => unscheduleEmailCampaign(campaign.id), t('emailCampaigns.toasts.unscheduled'))} />
      <${Button} title=${t('emailCampaigns.actions.sendTest')} icon="IconSend" size="small" variant="secondary" onClick=${() => setDialog('TEST')} />
      <${Button} title=${t('emailCampaigns.actions.cancel')} icon="IconPlayerStop" size="small" variant="secondary" accent="danger" onClick=${() => setDialog('CANCEL')} />
    </div>
    ${dialog === 'TEST' ? html`<${EmailCampaignTestDialog} campaignId=${campaign.id} campaignName=${campaign.name} onClose=${() => setDialog(null)} />` : null}
    ${dialog === 'CANCEL'
      ? html`<${Modal} title=${t('emailCampaigns.confirm.cancelTitle', { name: campaign.name })} subtitle=${t('emailCampaigns.confirm.cancelDescription')}
          confirmLabel=${t('emailCampaigns.actions.cancel')} confirmAccent="danger" isLoading=${isBusy} onClose=${() => setDialog(null)}
          onConfirm=${() => execute(() => cancelEmailCampaign(campaign.id), t('emailCampaigns.toasts.canceled'))} />`
      : null}
  </div>`;
}

/** Não enviada: o motivo e as saídas. @param {{ campaign: EmailCampaign, onChanged: (campaign: EmailCampaign) => void }} props */
function FailedView({ campaign, onChanged }) {
  const [isBusy, setIsBusy] = useState(false);
  const reason = campaign.failureReason && hasTranslation(`emailCampaigns.failureReasons.${campaign.failureReason}`) ? campaign.failureReason : 'UNKNOWN';
  /** @param {() => Promise<EmailCampaign>} run @param {string} message @param {(campaign: EmailCampaign) => void} then */
  const execute = async (run, message, then) => {
    setIsBusy(true);
    try {
      const result = await run();
      showSuccessSnackbar(message);
      then(result);
    } catch (error) {
      showErrorSnackbar(error);
    } finally {
      setIsBusy(false);
    }
  };
  return html`<div class="ec-failed" data-testid="email-campaign-failed">
    <div class="ec-notice" data-tone="red" role="alert">
      <${Icon} name="IconAlertTriangle" size=${18} />
      <div>
        <strong>${t('emailCampaigns.failed.title')}</strong>
        <p>${t(`emailCampaigns.failureReasons.${reason}`)}</p>
        <p class="ec-muted">${t('emailCampaigns.failed.hint')}</p>
      </div>
    </div>
    <div class="ec-button-row">
      <${Button} title=${t('emailCampaigns.actions.backToDraft')} icon="IconArrowBackUp" size="small" accent="blue" isLoading=${isBusy}
        onClick=${() => execute(() => backToDraftEmailCampaign(campaign.id), t('emailCampaigns.toasts.backToDraft'), onChanged)} />
      <${Button} title=${t('emailCampaigns.actions.duplicate')} icon="IconCopy" size="small" variant="secondary"
        onClick=${() => execute(() => duplicateEmailCampaign(campaign.id), t('emailCampaigns.toasts.duplicated'), (copy) => navigate(`/campaigns/email/${copy.id}`))} />
    </div>
  </div>`;
}

/** @param {{ campaignId: string }} props */
export function CampaignEditorPage({ campaignId }) {
  const [campaign, setCampaign] = useState(/** @type {EmailCampaign | null} */ (null));
  const [upgrade, setUpgrade] = useState(/** @type {{ notes: string[], legacy: any } | null} */ (null));
  const [state, setState] = useState(/** @type {'LOADING' | 'READY' | 'NOT_FOUND' | 'FAILED'} */ ('LOADING'));
  const [version, setVersion] = useState(0);

  const load = async () => {
    setState('LOADING');
    try {
      let loaded = await getEmailCampaign(campaignId);
      // rascunho da 1.13.0: vira v2 no lugar ao abrir (spec/email.md seção 18.2)
      if (loaded.formatVersion === 1 && loaded.status === 'DRAFT') {
        const result = await upgradeEmailCampaign(campaignId);
        loaded = result.campaign;
        setUpgrade({ notes: result.notes ?? [], legacy: result.legacy ?? null });
      }
      setCampaign(loaded);
      setState('READY');
    } catch (error) {
      setState(error instanceof ApiError && error.statusCode === 404 ? 'NOT_FOUND' : 'FAILED');
    }
  };
  useEffect(() => {
    load();
    loadWorkspaceMembers();
    loadEmailSettings();
  }, [campaignId]);
  useEffect(() => {
    if (campaign) document.title = `${campaign.name} · ${t('emailCampaigns.title')}`;
  }, [campaign?.name]);

  /** A situação mudou (enviar, agendar, desagendar): a vista troca. @param {EmailCampaign} next */
  const replace = (next) => {
    setCampaign(next);
    setVersion((value) => value + 1);
  };

  const suspension = /** @type {any} */ (emailSettingsState.value)?.suspension;
  const back = html`<${Link} href=${LIST_PATH} className="ec-back-link"><${Icon} name="IconChevronLeft" size=${14} />${t('emailCampaigns.wizard.backToList')}<//>`;
  if (state === 'NOT_FOUND') {
    return html`<${PageCard} header=${html`<${PageHeader} icon="IconMail" title=${t('emailCampaigns.title')} />`}>
      <div class="ec-page"><${EmptyState} icon="IconMail" title=${t('emailCampaigns.notFound')}
        action=${html`<${Button} title=${t('emailCampaigns.backToList')} size="small" variant="secondary" onClick=${() => navigate(LIST_PATH)} />`} /></div>
    <//>`;
  }
  if (state === 'FAILED') {
    return html`<${PageCard} header=${html`<${PageHeader} icon="IconMail" title=${t('emailCampaigns.title')} />`}>
      <div class="ec-page"><div class="ec-inline-error" role="alert"><span>${t('emailCampaigns.pageLoadError')}</span>
        <${Button} title=${t('emailCampaigns.retry')} size="small" variant="secondary" onClick=${load} /></div></div>
    <//>`;
  }
  if (!campaign || state === 'LOADING') {
    return html`<${PageCard} header=${html`<${PageHeader} icon="IconMail" title=${t('emailCampaigns.title')} />`}>
      <div class="ec-page"><${Skeleton} height=${48} radius=${8} /><${Skeleton} height=${320} radius=${8} /></div>
    <//>`;
  }
  const view = emailCampaignPageView(campaign);
  const actions = html`<div class="ec-header-actions">
    <${EmailCampaignStatusBadge} status=${campaign.status} isLegacy=${campaign.formatVersion === 1} />
    <${EmailCampaignActionsMenu} campaign=${campaign} exclude=${['open', 'report']}
      onChanged=${(/** @type {EmailCampaign | null} */ changed) => (changed ? replace(changed) : navigate(LIST_PATH))} />
  </div>`;
  return html`<${PageCard} header=${html`<${PageHeader} icon="IconMail" title=${campaign.name} actions=${actions} />`}>
    <div class="ec-page ec-campaign-page" data-view=${view}>
      ${back}
      ${suspension ? html`<p class="ec-notice" data-tone="red" role="alert"><${Icon} name="IconAlertTriangle" size=${16} /><span>${t('emailCampaigns.suspendedBanner')}</span></p>` : null}
      ${view === 'WIZARD' || view === 'UPGRADE'
        ? html`<${EmailCampaignWizard} key=${`${campaign.id}-${version}`} initialCampaign=${campaign} upgrade=${upgrade} onCampaignReplaced=${replace}
            onSaved=${setCampaign} />`
        : null}
      ${view === 'SCHEDULED' ? html`<${ScheduledView} campaign=${campaign} onChanged=${replace} />` : null}
      ${view === 'FAILED' ? html`<${FailedView} campaign=${campaign} onChanged=${replace} />` : null}
      ${view === 'REPORT' ? html`<${EmailCampaignReport} campaign=${campaign} onCampaignChange=${setCampaign} />` : null}
    </div>
  <//>`;
}
